import { useEffect, useMemo, useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_KEYS, PORTAL_META } from '../contexts/MarketContext';
import useStatusPipeline from '../hooks/useStatusPipeline';
import {
  TOUR_CATEGORIES, CATEGORY_LABELS, CATEGORY_COLORS, CATEGORY_STAGES,
  PIPELINE_SETTINGS_DOC, LOCKED_STAGE_KEYS, getAllCategoryStages,
} from '../utils/statuses';

function newStageKey() {
  return `custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

// Every category's full item list (removed ones included) for one portal.
function portalDraft(config, portal) {
  const out = {};
  TOUR_CATEGORIES.forEach((cat) => {
    out[cat] = getAllCategoryStages(cat, config, portal).map((s) => ({ ...s }));
  });
  return out;
}

/**
 * Admin Settings → Status Pipeline. Edits each portal's checklist items per
 * pipeline stage, saved to settings/statusPipeline (see statuses.js).
 */
export default function StatusPipelineSettings() {
  const { currentUser } = useAuth();
  const { config, loaded } = useStatusPipeline();
  const [portal, setPortal] = useState(PORTAL_KEYS[0]);
  const [draft, setDraft] = useState(null);
  const [newLabels, setNewLabels] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const saved = useMemo(() => (loaded ? portalDraft(config, portal) : null), [config, loaded, portal]);
  useEffect(() => { setDraft(saved); setNewLabels({}); }, [saved]);

  const dirty = draft && saved && JSON.stringify(draft) !== JSON.stringify(saved);

  function switchPortal(p) {
    if (p === portal) return;
    if (dirty && !window.confirm('You have unsaved changes for this portal. Discard them?')) return;
    setPortal(p);
    setSavedAt(null);
  }

  function updateCat(cat, fn) {
    setDraft((d) => ({ ...d, [cat]: fn(d[cat]) }));
  }

  function rename(cat, key, label) {
    updateCat(cat, (items) => items.map((s) => (s.key === key ? { ...s, label } : s)));
  }

  // Moves an item past its nearest visible neighbour (removed items keep their slot).
  function move(cat, key, dir) {
    updateCat(cat, (items) => {
      const list = [...items];
      const i = list.findIndex((s) => s.key === key);
      let j = i + dir;
      while (j >= 0 && j < list.length && list[j].removed) j += dir;
      if (j < 0 || j >= list.length) return items;
      [list[i], list[j]] = [list[j], list[i]];
      return list;
    });
  }

  function setRemoved(cat, key, removed) {
    updateCat(cat, (items) => items.map((s) => {
      if (s.key !== key) return s;
      const { removed: _ignored, ...rest } = s;
      return removed ? { ...rest, removed: true } : rest;
    }));
  }

  function addItem(cat) {
    const label = (newLabels[cat] || '').trim();
    if (!label) return;
    updateCat(cat, (items) => [...items, { key: newStageKey(), label }]);
    setNewLabels((n) => ({ ...n, [cat]: '' }));
  }

  // Back to the built-in list. Items admins added are kept as removed, so
  // re-adding them later (via Restore) brings back every tour's ticks.
  function resetCat(cat) {
    if (!window.confirm(`Reset "${CATEGORY_LABELS[cat]}" to the default checklist?`)) return;
    updateCat(cat, (items) => {
      const defaults = (CATEGORY_STAGES[cat] || []).map((s) => ({ ...s }));
      const defaultKeys = new Set(defaults.map((s) => s.key));
      const extras = items.filter((s) => !defaultKeys.has(s.key)).map((s) => ({ ...s, removed: true }));
      return [...defaults, ...extras];
    });
  }

  async function save() {
    const blank = TOUR_CATEGORIES.find((cat) => draft[cat].some((s) => !s.removed && !s.label.trim()));
    if (blank) { window.alert(`Every item in "${CATEGORY_LABELS[blank]}" needs a name.`); return; }
    setSaving(true);
    try {
      const cleaned = {};
      TOUR_CATEGORIES.forEach((cat) => {
        cleaned[cat] = draft[cat].map((s) => ({ ...s, label: s.label.trim() }));
      });
      await setDoc(doc(db, ...PIPELINE_SETTINGS_DOC), {
        [portal]: cleaned,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || '',
      }, { merge: true });
      setSavedAt(new Date());
    } catch (err) {
      console.error('Failed to save status pipeline:', err);
      window.alert('Could not save the status pipeline. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
      <div className="mb-4">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Status Pipeline</h3>
        <p className="text-[12px] text-[#9CA3AF]">
          The checklist shown under each stage of a tour's Status Pipeline. Each portal has its own list. Renaming an item keeps
          every tour's ticks. A new item starts unticked, so tours currently in that stage will need it ticked before they can advance.
          Items marked 🔒 drive other features (customer status, dashboard counts, auto-archive) and can be renamed but not removed.
        </p>
      </div>

      {/* Portal picker */}
      <div className="flex flex-wrap gap-2 mb-5">
        {PORTAL_KEYS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => switchPortal(p)}
            className={`px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold border transition-colors ${
              portal === p ? 'bg-navy text-white border-navy' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB]'
            }`}
          >
            {PORTAL_META[p].label}
          </button>
        ))}
      </div>

      {!draft ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : (
        <div className="space-y-4">
          {TOUR_CATEGORIES.map((cat) => {
            const items = draft[cat];
            const visible = items.filter((s) => !s.removed);
            const removed = items.filter((s) => s.removed);
            return (
              <div key={cat} className="rounded-[10px] border border-[#E5E7EB] overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <span className="text-[13px] font-bold" style={{ color: CATEGORY_COLORS[cat] }}>{CATEGORY_LABELS[cat]}</span>
                  <button type="button" onClick={() => resetCat(cat)} className="text-[11.5px] text-[#9CA3AF] hover:text-[#374151]">
                    Reset to default
                  </button>
                </div>
                <div className="px-4 py-3 space-y-1.5">
                  {visible.map((s, idx) => {
                    const locked = LOCKED_STAGE_KEYS.has(s.key);
                    return (
                      <div key={s.key} className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <button type="button" onClick={() => move(cat, s.key, -1)} disabled={idx === 0}
                            className="text-[10px] leading-none px-1 text-[#9CA3AF] hover:text-[#374151] disabled:opacity-30" aria-label="Move up">▲</button>
                          <button type="button" onClick={() => move(cat, s.key, 1)} disabled={idx === visible.length - 1}
                            className="text-[10px] leading-none px-1 text-[#9CA3AF] hover:text-[#374151] disabled:opacity-30" aria-label="Move down">▼</button>
                        </div>
                        <input
                          value={s.label}
                          onChange={(e) => rename(cat, s.key, e.target.value)}
                          className="flex-1 min-w-0 border border-[#E5E7EB] rounded-[8px] px-3 py-[6px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                        />
                        {locked ? (
                          <span className="w-[64px] text-center text-[12px] text-[#9CA3AF]" title="Used by other features, so it can't be removed">🔒</span>
                        ) : (
                          <button type="button" onClick={() => setRemoved(cat, s.key, true)}
                            className="w-[64px] text-[12px] font-semibold text-dr/70 hover:text-dr">Remove</button>
                        )}
                      </div>
                    );
                  })}

                  <div className="flex items-center gap-2 pt-1.5">
                    <span className="w-[18px]" />
                    <input
                      value={newLabels[cat] || ''}
                      onChange={(e) => setNewLabels((n) => ({ ...n, [cat]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') addItem(cat); }}
                      placeholder="Add a checklist item…"
                      className="flex-1 min-w-0 border border-dashed border-[#D1D5DB] rounded-[8px] px-3 py-[6px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                    />
                    <button type="button" onClick={() => addItem(cat)} disabled={!(newLabels[cat] || '').trim()}
                      className="w-[64px] text-[12px] font-semibold text-accent disabled:opacity-40">Add</button>
                  </div>

                  {removed.length > 0 && (
                    <div className="pt-2 mt-1 border-t border-[#F1F3F6]">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.7px] text-[#9CA3AF] mb-1">Removed</p>
                      {removed.map((s) => (
                        <div key={s.key} className="flex items-center justify-between gap-2 py-0.5">
                          <span className="text-[12.5px] text-[#9CA3AF] line-through truncate">{s.label}</span>
                          <button type="button" onClick={() => setRemoved(cat, s.key, false)}
                            className="text-[12px] font-semibold text-accent flex-shrink-0">Restore</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-end gap-3 pt-1">
            {savedAt && !dirty && <span className="text-[12px] text-sg font-semibold">Saved ✓</span>}
            {dirty && (
              <button type="button" onClick={() => setDraft(saved)} disabled={saving}
                className="text-[12.5px] font-semibold text-[#6B7280] hover:text-[#374151]">Discard changes</button>
            )}
            <button type="button" onClick={save} disabled={!dirty || saving}
              className="px-4 py-2 rounded-[9px] text-[12.5px] font-bold text-white bg-navy hover:opacity-90 disabled:opacity-50">
              {saving ? 'Saving…' : `Save ${PORTAL_META[portal].label} pipeline`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
